/**
 * 知识抽查系统API服务
 * 封装所有API接口调用
 */
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

const baseUrl = getApiBaseUrl();
const apiBase = `${baseUrl}/api/knowledge-lottery`;

/**
 * 创建axios实例
 */
const api = axios.create({
  baseURL: apiBase,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * 请求拦截器
 */
api.interceptors.request.use(
  (config) => {
    // 可以在这里添加认证token等
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器
 */
api.interceptors.response.use(
  (response) => {
    // 直接返回 response.data，因为后端返回的格式是 { success: true, data: ..., ... }
    return response.data;
  },
  (error) => {
    // 处理错误响应
    if (error.response) {
      const errorData = error.response.data;
      const message = errorData?.message || errorData?.error || error.message || '请求失败';
      return Promise.reject(new Error(message));
    } else if (error.request) {
      return Promise.reject(new Error('网络错误，请检查服务器是否运行'));
    } else {
      return Promise.reject(new Error(error.message || '请求失败'));
    }
  }
);

/**
 * 成员管理API
 */
export const memberApi = {
  /**
   * 获取所有成员
   */
  getAllMembers: () => {
    return api.get('/members');
  },

  /**
   * 获取可用成员（未请假）
   */
  getAvailableMembers: () => {
    return api.get('/members/available');
  },

  /**
   * 获取单个成员
   */
  getMemberById: (id) => {
    return api.get(`/members/${id}`);
  },

  /**
   * 创建成员
   */
  createMember: (data) => {
    return api.post('/members', data);
  },

  /**
   * 更新成员
   */
  updateMember: (id, data) => {
    return api.put(`/members/${id}`, data);
  },

  /**
   * 删除成员
   */
  deleteMember: (id) => {
    return api.delete(`/members/${id}`);
  }
};

/**
 * 分类管理API
 */
export const categoryApi = {
  /**
   * 获取所有分类
   */
  getAllCategories: () => {
    return api.get('/categories');
  },

  /**
   * 获取单个分类
   */
  getCategoryById: (id) => {
    return api.get(`/categories/${id}`);
  },

  /**
   * 创建分类
   */
  createCategory: (data) => {
    return api.post('/categories', data);
  },

  /**
   * 更新分类
   */
  updateCategory: (id, data) => {
    return api.put(`/categories/${id}`, data);
  },

  /**
   * 删除分类
   */
  deleteCategory: (id) => {
    return api.delete(`/categories/${id}`);
  }
};

/**
 * 知识点管理API
 */
export const knowledgeContentApi = {
  /**
   * 获取所有知识点
   */
  getAllKnowledgeContents: (params = {}) => {
    return api.get('/knowledge-contents', { params });
  },

  /**
   * 获取单个知识点
   */
  getKnowledgeContentById: (id) => {
    return api.get(`/knowledge-contents/${id}`);
  },

  /**
   * 创建知识点
   */
  createKnowledgeContent: (data) => {
    return api.post('/knowledge-contents', data);
  },

  /**
   * 更新知识点
   */
  updateKnowledgeContent: (id, data) => {
    return api.put(`/knowledge-contents/${id}`, data);
  },

  /**
   * 删除知识点
   */
  deleteKnowledgeContent: (id) => {
    return api.delete(`/knowledge-contents/${id}`);
  }
};

/**
 * 题目管理API
 */
export const quizQuestionApi = {
  /**
   * 获取所有题目
   */
  getAllQuizQuestions: (params = {}) => {
    return api.get('/quiz-questions', { params });
  },

  /**
   * 获取单个题目
   */
  getQuizQuestionById: (id) => {
    return api.get(`/quiz-questions/${id}`);
  },

  /**
   * 创建题目
   */
  createQuizQuestion: (data) => {
    return api.post('/quiz-questions', data);
  },

  /**
   * 更新题目
   */
  updateQuizQuestion: (id, data) => {
    return api.put(`/quiz-questions/${id}`, data);
  },

  /**
   * 删除题目
   */
  deleteQuizQuestion: (id) => {
    return api.delete(`/quiz-questions/${id}`);
  }
};

/**
 * 会议管理API
 */
export const sessionApi = {
  /**
   * 获取所有会议
   */
  getAllSessions: () => {
    return api.get('/sessions');
  },

  /**
   * 获取单个会议详情
   */
  getSessionById: (id) => {
    return api.get(`/sessions/${id}`);
  },

  /**
   * 创建会议
   */
  createSession: (data) => {
    return api.post('/sessions', data);
  },

  /**
   * 更新会议
   */
  updateSession: (id, data) => {
    return api.put(`/sessions/${id}`, data);
  },

  /**
   * 删除会议
   */
  deleteSession: (id) => {
    return api.delete(`/sessions/${id}`);
  },

  /**
   * 执行抽签分配
   */
  assignQuestions: (sessionId) => {
    return api.post(`/sessions/${sessionId}/assign`);
  }
};

/**
 * 分组管理API
 */
export const groupApi = {
  /**
   * 获取所有分组
   */
  getAllGroups: () => {
    return api.get('/groups');
  },

  /**
   * 获取单个分组详情
   */
  getGroupById: (id) => {
    return api.get(`/groups/${id}`);
  },

  /**
   * 创建分组
   */
  createGroup: (data) => {
    return api.post('/groups', data);
  },

  /**
   * 更新分组
   */
  updateGroup: (id, data) => {
    return api.put(`/groups/${id}`, data);
  },

  /**
   * 删除分组
   */
  deleteGroup: (id) => {
    return api.delete(`/groups/${id}`);
  },

  /**
   * 获取分组中的可用成员（排除请假的）
   */
  getAvailableMembers: (groupId) => {
    return api.get(`/groups/${groupId}/members/available`);
  }
};

/**
 * 统计API
 */
export const statisticsApi = {
  /**
   * 获取总体统计
   */
  getOverview: () => {
    return api.get('/statistics/overview');
  },

  /**
   * 获取成员统计
   */
  getMemberStatistics: () => {
    return api.get('/statistics/members');
  },

  /**
   * 获取会议统计
   */
  getSessionStatistics: () => {
    return api.get('/statistics/sessions');
  },

  /**
   * 获取题目统计
   */
  getQuestionStatistics: () => {
    return api.get('/statistics/questions');
  }
};

/**
 * 分配管理API
 */
export const assignmentApi = {
  /**
   * 获取所有分配
   */
  getAllAssignments: (params = {}) => {
    return api.get('/assignments', { params });
  },

  /**
   * 获取单个分配
   */
  getAssignmentById: (id) => {
    return api.get(`/assignments/${id}`);
  },

  /**
   * 删除分配
   */
  deleteAssignment: (id) => {
    return api.delete(`/assignments/${id}`);
  }
};

/**
 * 答案管理API
 */
export const answerApi = {
  /**
   * 获取所有答案
   */
  getAllAnswers: (params = {}) => {
    return api.get('/answers', { params });
  },

  /**
   * 获取单个答案
   */
  getAnswerById: (id) => {
    return api.get(`/answers/${id}`);
  },

  /**
   * 创建答案
   */
  createAnswer: (data) => {
    return api.post('/answers', data);
  },

  /**
   * 更新答案
   */
  updateAnswer: (id, data) => {
    return api.put(`/answers/${id}`, data);
  },

  /**
   * 删除答案
   */
  deleteAnswer: (id) => {
    return api.delete(`/answers/${id}`);
  }
};

/**
 * 周学习记录API
 */
export const weeklyLearningApi = {
  /**
   * 获取所有周学习记录
   */
  getAllWeeklyLearnings: () => {
    return api.get('/weekly-learning');
  },

  /**
   * 根据周数获取周学习记录
   */
  getWeeklyLearningByWeek: (weekNumber) => {
    return api.get(`/weekly-learning/week/${weekNumber}`);
  },

  /**
   * 获取单个周学习记录
   */
  getWeeklyLearningById: (id) => {
    return api.get(`/weekly-learning/${id}`);
  },

  /**
   * 创建周学习记录
   */
  createWeeklyLearning: (data) => {
    return api.post('/weekly-learning', data);
  },

  /**
   * 更新周学习记录
   */
  updateWeeklyLearning: (id, data) => {
    return api.put(`/weekly-learning/${id}`, data);
  },

  /**
   * 删除周学习记录
   */
  deleteWeeklyLearning: (id) => {
    return api.delete(`/weekly-learning/${id}`);
  }
};

/**
 * 用户管理API（仅管理员）
 */
export const userManagementApi = {
  /**
   * 获取所有用户
   */
  getAllUsers: () => {
    return api.get('/users');
  },

  /**
   * 创建用户
   */
  createUser: (data) => {
    return api.post('/users', data);
  },

  /**
   * 更新用户
   */
  updateUser: (id, data) => {
    return api.put(`/users/${id}`, data);
  },

  /**
   * 删除用户
   */
  deleteUser: (id) => {
    return api.delete(`/users/${id}`);
  }
};

/**
 * 角色管理API
 */
export const roleApi = {
  /**
   * 获取所有角色
   */
  getAllRoles: () => {
    return api.get('/roles');
  },

  /**
   * 获取单个角色
   */
  getRoleById: (id) => {
    return api.get(`/roles/${id}`);
  }
};

/**
 * 用户角色关联API
 */
export const userRoleApi = {
  /**
   * 获取所有用户及其角色
   */
  getAllUserRoles: () => {
    return api.get('/user-roles');
  },

  /**
   * 获取单个用户的角色
   */
  getUserRoleByUserId: (userId) => {
    return api.get(`/user-roles/user/${userId}`);
  },

  /**
   * 为用户分配角色
   */
  assignRoleToUser: (data) => {
    return api.post('/user-roles', data);
  }
};

const apiExports = {
  memberApi,
  categoryApi,
  knowledgeContentApi,
  quizQuestionApi,
  sessionApi,
  assignmentApi,
  answerApi,
  weeklyLearningApi,
  groupApi,
  statisticsApi,
  userManagementApi,
  roleApi,
  userRoleApi
};

export default apiExports;
